import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  Paper,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  PlayCircle as InProgressIcon,
  RateReview as ReviewIcon,
  CheckCircle as DoneIcon,
  Warning as OverdueIcon,
  TrendingUp as TrendingIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
} from 'recharts';

const COLORS = {
  todo: '#64748b',
  in_progress: '#3b82f6',
  review: '#f59e0b',
  done: '#10b981',
  overdue: '#ef4444',
};

const PRIORITY_COLORS = {
  low: '#94a3b8',
  medium: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444',
};

const CATEGORY_COLORS = {
  marketing: '#8b5cf6',
  social: '#06b6d4',
  content: '#10b981',
  production: '#f59e0b',
  report: '#6366f1',
};

const CATEGORY_LABELS = {
  marketing: 'Marketing',
  social: 'Social Media',
  content: 'Content',
  production: 'Sản xuất',
  report: 'Báo cáo',
};

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'white',
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: entry.color,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {entry.name}: <strong>{entry.value}</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

// Custom Legend
const CustomLegend = ({ payload }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
    {payload.map((entry, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: entry.color,
          }}
        />
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
          {entry.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const TaskDashboard = ({ stats, loading }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">Không có dữ liệu</Typography>
      </Box>
    );
  }

  const statusCards = [
    {
      title: 'Chờ xử lý',
      value: stats.statusCounts?.todo || 0,
      icon: TaskIcon,
      color: COLORS.todo,
      bgColor: '#f1f5f9',
      gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    },
    {
      title: 'Đang làm',
      value: stats.statusCounts?.in_progress || 0,
      icon: InProgressIcon,
      color: COLORS.in_progress,
      bgColor: '#dbeafe',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    },
    {
      title: 'Chờ review',
      value: stats.statusCounts?.review || 0,
      icon: ReviewIcon,
      color: COLORS.review,
      bgColor: '#fef3c7',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    },
    {
      title: 'Hoàn thành',
      value: stats.statusCounts?.done || 0,
      icon: DoneIcon,
      color: COLORS.done,
      bgColor: '#d1fae5',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    },
  ];

  const pieData = [
    { name: 'Chờ xử lý', value: stats.statusCounts?.todo || 0, color: COLORS.todo },
    { name: 'Đang làm', value: stats.statusCounts?.in_progress || 0, color: COLORS.in_progress },
    { name: 'Chờ review', value: stats.statusCounts?.review || 0, color: COLORS.review },
    { name: 'Hoàn thành', value: stats.statusCounts?.done || 0, color: COLORS.done },
  ].filter(item => item.value > 0);

  const assigneeData = (stats.tasksByAssignee || []).slice(0, 6).map(user => ({
    name: (user.fullName || 'Chưa giao').split(' ').slice(-2).join(' '),
    fullName: user.fullName || 'Chưa giao',
    total: user.total,
    done: user.done,
    overdue: user.overdue,
    inProgress: user.total - user.done - user.overdue,
  }));

  // Completion rate radial data
  const completionRate = stats.completionRate || 0;
  const radialData = [
    { name: 'Hoàn thành', value: completionRate, fill: '#10b981' },
  ];

  // Category data for area chart
  const categoryData = (stats.categoryStats || []).map(cat => ({
    name: CATEGORY_LABELS[cat._id] || cat._id,
    value: cat.count,
    fill: CATEGORY_COLORS[cat._id] || '#667eea',
  }));

  return (
    <Box>
      {/* Status Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statusCards.map((card, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: 'none',
                overflow: 'visible',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ color: '#64748b', mb: 1, fontWeight: 500 }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 800, 
                        background: card.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      background: card.gradient,
                      width: 52,
                      height: 52,
                      boxShadow: `0 8px 20px ${card.color}40`,
                    }}
                  >
                    <card.icon sx={{ fontSize: 26 }} />
                  </Avatar>
                </Box>
              </CardContent>
              {/* Bottom accent */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: card.gradient,
                  borderRadius: '0 0 12px 12px',
                }}
              />
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Completion Rate - Radial Chart */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none',
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SpeedIcon sx={{ color: '#667eea', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Hiệu suất
              </Typography>
            </Box>

            <Box sx={{ position: 'relative', height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="90%"
                  data={radialData}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    background={{ fill: '#f1f5f9' }}
                    dataKey="value"
                    cornerRadius={10}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              {/* Center text */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -30%)',
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {completionRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hoàn thành
                </Typography>
              </Box>
            </Box>

            {/* Stats below */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fef2f2', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444' }}>
                    {stats.overdueCount || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Quá hạn
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
                    {stats.totalTasks || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Tổng task
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Status Distribution - Donut Chart */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none',
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
              Phân bố trạng thái
            </Typography>
            
            {pieData.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <defs>
                      {pieData.map((entry, index) => (
                        <linearGradient key={index} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                          <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                        </linearGradient>
                      ))}
                    </defs>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                      animationBegin={0}
                      animationDuration={1000}
                    >
                      {pieData.map((entry, index) => (
                        <Cell 
                          key={index} 
                          fill={`url(#gradient-${index})`}
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center text */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '45%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>
                    {stats.totalTasks || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Task
                  </Typography>
                </Box>
                <CustomLegend payload={pieData.map(d => ({ value: d.name, color: d.color }))} />
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <TaskIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                <Typography color="text.secondary">Chưa có task</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: 'none',
              height: '100%',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
              Theo phân loại
            </Typography>

            {categoryData.length > 0 ? (
              <Box>
                {categoryData.map((cat, index) => (
                  <Box key={index} sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: cat.fill,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#475569' }}>
                          {cat.name}
                        </Typography>
                      </Box>
                      <Chip
                        label={cat.value}
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          bgcolor: cat.fill + '20',
                          color: cat.fill,
                        }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(cat.value / (stats.totalTasks || 1)) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#f1f5f9',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: `linear-gradient(90deg, ${cat.fill} 0%, ${cat.fill}99 100%)`,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Chưa có dữ liệu</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tasks by Assignee - Card Style */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: 'none',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
          Task theo người phụ trách
        </Typography>

        {assigneeData.length > 0 ? (
          <Grid container spacing={2}>
            {assigneeData.map((user, index) => {
              const completionRate = user.total > 0 ? Math.round((user.done / user.total) * 100) : 0;
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      bgcolor: 'white',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                        borderColor: '#667eea',
                      },
                    }}
                  >
                    {/* User Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {user.fullName?.[0] || 'U'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600, 
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {user.fullName || 'Chưa giao'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {user.total} task được giao
                        </Typography>
                      </Box>
                    </Box>

                    {/* Progress Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Tiến độ
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981' }}>
                          {completionRate}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#f1f5f9',
                          overflow: 'hidden',
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(user.done / (user.total || 1)) * 100}%`,
                            background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                            borderRadius: 4,
                          }}
                        />
                        <Box
                          sx={{
                            width: `${(user.inProgress / (user.total || 1)) * 100}%`,
                            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                          }}
                        />
                        <Box
                          sx={{
                            width: `${(user.overdue / (user.total || 1)) * 100}%`,
                            background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Stats */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        label={`${user.done} xong`}
                        size="small"
                        sx={{
                          height: 24,
                          bgcolor: '#d1fae5',
                          color: '#059669',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      <Chip
                        label={`${user.inProgress} đang làm`}
                        size="small"
                        sx={{
                          height: 24,
                          bgcolor: '#dbeafe',
                          color: '#2563eb',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                      {user.overdue > 0 && (
                        <Chip
                          label={`${user.overdue} trễ`}
                          size="small"
                          sx={{
                            height: 24,
                            bgcolor: '#fee2e2',
                            color: '#dc2626',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <TaskIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
            <Typography color="text.secondary">Chưa có dữ liệu</Typography>
          </Box>
        )}
      </Paper>

      {/* Overdue Tasks Alert */}
      {stats.overdueTasks && stats.overdueTasks.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mt: 3,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(239,68,68,0.15)',
            border: '1px solid #fecaca',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', mr: 2 }}>
              <OverdueIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>
                Task quá hạn cần xử lý
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.overdueTasks.length} task cần được hoàn thành ngay
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {stats.overdueTasks.slice(0, 6).map((task, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2.5,
                    border: '1px solid #fecaca',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {task.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label={task.priority}
                      size="small"
                      sx={{
                        height: 22,
                        bgcolor: PRIORITY_COLORS[task.priority] + '20',
                        color: PRIORITY_COLORS[task.priority],
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 500 }}>
                      {new Date(task.deadline).toLocaleDateString('vi-VN')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default TaskDashboard;
